import React from 'react';
import styled from 'styled-components';
import { Body5, Container, H5 } from '@ui';
import { ResultOnly } from '@containers/DigitalAnalyst/components/AnswerLine';
import { ROUTES } from '@constants/routes';
import { SkeletonButton } from '@ui/buttons/SkeletonButton';

const Wrapper = styled.div`
  padding: 16px 0 0 0;
`;

const OverviewTable = styled.table`
  border-radius: 4px;
  width: 100%;

  td {
    vertical-align: top;

    border: 1px solid ${({ theme }) => theme.themeColors.text};
    padding: 4px 8px;
    width: 50%;
  }
`;
const ItemTableWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
`;
const ItemTable = styled.table`
  margin-top: 24px;

  border-radius: 4px;
  //width: 100%;
  table-layout: fixed;

  th,
  td {
    border: 1px solid ${({ theme }) => theme.themeColors.text};
    padding: 4px 8px;
  }

  td {
    vertical-align: center;
    width: 120px;
  }

  td:nth-child(1),
  td:nth-child(3) {
    min-width: 280px;
  }

  th:nth-child(2),
  th:nth-child(4),
  td:nth-child(2),
  td:nth-child(4) {
    text-align: center;
  }

  td:nth-child(2),
  td:nth-child(4) {
    width: 280px;
  }
`;

const MiscTable = styled.table`
  margin-top: 24px;

  border-radius: 4px;
  width: 100%;

  td {
    vertical-align: top;

    border: 1px solid ${({ theme }) => theme.themeColors.text};
    padding: 4px 8px;
    width: 50%;
  }
`;

const KeyValue = ({ keyName, templateKey, answers }) => {
  const answerValue = answers[templateKey] || 'Working...';

  return (
    <Container>
      <Body5 bold>{keyName}: </Body5>
      {templateKey ? <ResultOnly result={answerValue} /> : null}
    </Container>
  );
};

const YNU = ({ answers, templateKey }) => {
  const answerValue = answers[templateKey] || 'Working...';
  if (!templateKey) return null;
  return <ResultOnly result={answerValue} />;
};

const TextAnswer = ({ answers, templateKey }) => {
  const answerValue = answers[templateKey] || 'Working...';
  return <ResultOnly result={answerValue} />;
};

export const NewDealEvaluation = ({ answers, selectedDocs }) => {
  if (!answers) {
    return null;
  }

  const onQueryDocumentsClick = () => {
    const url = ROUTES.digitalAnalyst + `?doc-ids=${selectedDocs.map((d) => d.id).join(',')}`;
    window.open(url, '_blank');
  };

  return (
    <Wrapper>
      <Container row verticallyCenter spaceBetween bottom={24}>
        <H5>Confidential</H5>
        <Container left={8}>
          <SkeletonButton onClick={onQueryDocumentsClick}>Query Documents</SkeletonButton>
        </Container>
      </Container>
      <OverviewTable>
        <tbody>
          <tr>
            <td>
              <KeyValue keyName={'Company Name'} answers={answers} templateKey={'{{TEMPLATE_1}}'} />
            </td>
            <td>
              <Container row verticallyCentered>
                <KeyValue keyName={'Date Received'} answers={answers} templateKey={'{{TEMPLATE_2}}'} />
                <Body5>&nbsp;&nbsp;//&nbsp;&nbsp;</Body5>
                <KeyValue keyName={'Date Completed'} answers={answers} templateKey={'{{TEMPLATE_3}}'} />
              </Container>
            </td>
          </tr>
          <tr>
            <td>
              <Container row verticallyCentered>
                <KeyValue keyName={'Seller'} answers={answers} templateKey={'{{TEMPLATE_4}}'} />
                <Body5>&nbsp;&nbsp;//&nbsp;&nbsp;</Body5>
                <KeyValue keyName={'Banker'} answers={answers} templateKey={'{{TEMPLATE_5}}'} />
              </Container>
            </td>
            <td>
              <KeyValue keyName={'Sponsors to Date'} answers={answers} templateKey={'{{TEMPLATE_6}}'} />
            </td>
          </tr>
          <tr>
            <td>
              <KeyValue keyName={'Triage Team'} answers={answers} templateKey={'{{TEMPLATE_7}}'} />
            </td>
            <td>
              <KeyValue keyName={'Incumbent Lenders'} answers={answers} templateKey={'{{TEMPLATE_8}}'} />
            </td>
          </tr>
          <tr>
            <td>
              <Container row verticallyCentered>
                <KeyValue keyName={'Year Founded'} answers={answers} templateKey={'{{TEMPLATE_9}}'} />
                <Body5>&nbsp;&nbsp;//&nbsp;&nbsp;</Body5>
                <KeyValue keyName={'Headquarters'} answers={answers} templateKey={'{{TEMPLATE_10}}'} />
              </Container>
            </td>
            <td>
              <KeyValue keyName={'Process Timetable'} answers={answers} templateKey={'{{TEMPLATE_11}}'} />
            </td>
          </tr>

          <tr>
            <td colSpan={2}>
              <KeyValue keyName={'Overview'} answers={answers} templateKey={'{{TEMPLATE_14}}'} />
              <Container top={8} left={16}>
                <TextAnswer answers={answers} templateKey={'{{TEMPLATE_15}}'} />
              </Container>
              <Container top={8} left={16}>
                <KeyValue keyName={'FYE Revenue / Adj. EBITDA'} answers={answers} templateKey={'{{TEMPLATE_16}}'} />
              </Container>
            </td>
          </tr>
          <tr>
            <td>
              <Container top={8}>
                <Body5 bold>Financials: </Body5>
              </Container>
              <Container top={8}>
                <TextAnswer answers={answers} templateKey={'{{TEMPLATE_17}}'} />
              </Container>
            </td>
            <td>
              <Container top={8}>
                <Body5 bold>Financial Notes: </Body5>
              </Container>
              <Container top={8}>
                <TextAnswer answers={answers} templateKey={'{{TEMPLATE_18}}'} />
              </Container>
            </td>
          </tr>
        </tbody>
      </OverviewTable>
      <ItemTableWrapper>
        <ItemTable>
          <thead>
            <tr>
              <th>Category</th>
              <th>
                <Container>Risk/Exposure</Container>
                <Container>(Low/High/TBD)</Container>
              </th>
              <th>Category</th>
              <th>
                <Container>Risk/Exposure</Container>
                <Container>(Low/High/TBD)</Container>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <Body5 bold>Customer Concentrations</Body5>
              </td>
              <td>
                <YNU answers={answers} templateKey={'{{TEMPLATE_19}}'} />
              </td>
              <td>
                <Body5 bold>Raw Materials</Body5>
              </td>
              <td>
                <YNU answers={answers} templateKey={'{{TEMPLATE_22}}'} />
              </td>
            </tr>
            <tr>
              <td>
                <Body5 bold>Cyclicality</Body5>
              </td>
              <td>
                <YNU answers={answers} templateKey={'{{TEMPLATE_31}}'} />
              </td>
              <td>
                <Body5 bold>FX Exposure</Body5>
              </td>
              <td>
                <YNU answers={answers} templateKey={'{{TEMPLATE_34}}'} />
              </td>
            </tr>
            <tr>
              <td>
                <Body5 bold>Seasonality</Body5>
              </td>
              <td>
                <YNU answers={answers} templateKey={'{{TEMPLATE_43}}'} />
              </td>

              <td>
                <Body5 bold>China Exposure</Body5>
              </td>
              <td>
                <YNU answers={answers} templateKey={'{{TEMPLATE_46}}'} />
              </td>
            </tr>
            <tr>
              <td>
                <Body5 bold>Project Based</Body5>
              </td>
              <td>
                <YNU answers={answers} templateKey={'{{TEMPLATE_52}}'} />
              </td>
              <td>
                <Body5 bold>Legal/Legislative</Body5>
              </td>
              <td>
                <YNU answers={answers} templateKey={'{{TEMPLATE_55}}'} />
              </td>
            </tr>
            <tr>
              <td>
                <Body5 bold>High Fixed Costs</Body5>
              </td>
              <td>
                <YNU answers={answers} templateKey={'{{TEMPLATE_28}}'} />
              </td>
              <td>
                <Body5 bold>Leverage</Body5>
              </td>
              <td>
                <YNU answers={answers} templateKey={'{{TEMPLATE_33}}'} />
              </td>
            </tr>
          </tbody>
        </ItemTable>
      </ItemTableWrapper>
      <MiscTable>
        <tbody>
          <tr>
            <td>
              <Container>
                <Body5 bold>Strengths: </Body5>
              </Container>
              <Container top={8} left={16}>
                <TextAnswer answers={answers} templateKey={'{{TEMPLATE_61}}'} />
              </Container>
            </td>

            <td>
              <Container>
                <Body5 bold>Areas of Concern: </Body5>
              </Container>
              <Container top={8} left={16}>
                <TextAnswer answers={answers} templateKey={'{{TEMPLATE_62}}'} />
              </Container>
            </td>
          </tr>
        </tbody>
      </MiscTable>
    </Wrapper>
  );
};
